import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  RefreshCw,
  Loader2,
  Trash2,
  ArrowLeft,
  Save,
  Inbox,
  Building2,
  Target,
  Sparkles,
  Settings2,
  Plus,
  X,
  Calendar,
  Globe,
  AlertTriangle,
} from "lucide-react";
import {
  subscribeCampaign,
  updateCampaign,
  deleteCampaign,
  deleteLeadsByCampaign,
  subscribeLeads,
  collavueSyncCost,
  collavueRedditSurcharge,
  dailySyncState,
  CAMPAIGN_GOALS,
  LEAD_PLATFORMS,
  campaignPlatforms,
  campaignPlatformsLabel,
} from "../../services/collavueStore";
import { syncCampaign } from "../../services/collavueApi";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastProvider";
import PageHeader from "../../components/collavue/PageHeader";
import SyncAnimationOverlay from "../../components/collavue/SyncAnimationOverlay";
import ConfirmDialog from "../../components/collavue/ConfirmDialog";

const FILTER_STORAGE_KEY = "collavue.leads.campaignFilter";

export default function CampaignDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { push } = useToast();
  const { userProfile } = useAuth();
  const [params, setParams] = useSearchParams();
  const [campaign, setCampaign] = useState(null);
  const [savedCampaign, setSavedCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [leads, setLeads] = useState([]);
  const [syncOpen, setSyncOpen] = useState(false);
  const [confirmSyncOpen, setConfirmSyncOpen] = useState(false);
  const [unsavedSyncOpen, setUnsavedSyncOpen] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [askDelete, setAskDelete] = useState(false);
  const [deleteLeadsToo, setDeleteLeadsToo] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const autoSyncFiredRef = useRef(false);
  const changesMadeToastShown = useRef(false)
  const touchedRef = useRef(new Set());

  // `savedCampaign` mirrors Firestore live; `campaign` is the edit buffer and
  // is only seeded from the first snapshot. A one-shot read here used to let
  // the editor hold a copy from page load and write all of it back on save —
  // silently re-enabling daily auto-sync that had since been turned off from
  // the digest email's one-click link, another tab, or the Firestore console.
  useEffect(() => {
    setLoading(true);
    touchedRef.current = new Set();
    const unsubCampaign = subscribeCampaign(id, (c) => {
      setSavedCampaign(c);
      setCampaign((prev) => {
        if (!prev || !c) return c;
        // Fold in every field the user has NOT edited, so a change made
        // elsewhere shows up in the form instead of sitting behind a stale
        // copy; in-flight edits are kept.
        const merged = { ...c };
        for (const key of touchedRef.current) merged[key] = prev[key];
        return merged;
      });
      setLoading(false);
    });
    const unsubLeads = subscribeLeads({ campaignId: id }, setLeads);
    return () => {
      unsubCampaign();
      unsubLeads();
    };
  }, [id]);

  // Only fields the user actually edited are written back, so a save never
  // resurrects a value the server changed while this page was open.
  const buildPatch = useCallback(() => {
    if (!campaign) return {};
    const patch = {};
    for (const key of touchedRef.current) {
      if (key === "id" || key === "createdAt" || key === "updatedAt") continue;
      patch[key] = campaign[key];
    }
    return patch;
  }, [campaign]);

  const onSync = useCallback(async () => {
    setSyncing(true);
    setSyncOpen(true);
    setSyncResult(null);
    setSyncError(null);
    try {
      // Persist any in-flight edits (e.g. date range) before the resync runs
      // so the back-end uses the latest settings. This must be awaited —
      // fire-and-forget let the sync read the OLD settings and charge a
      // different cost than the one shown in the confirmation dialog.
      const presyncPatch = buildPatch();
      if (Object.keys(presyncPatch).length) {
        try {
          await updateCampaign(id, presyncPatch);
          touchedRef.current = new Set();
        } catch (saveErr) {
          console.warn("pre-sync save failed:", saveErr);
        }
      }
      const r = await syncCampaign(id);
      setSyncResult(r);
      push({
        title: "Sync complete",
        desc: `${r.stats?.leadsCreated || 0} new leads, ${r.stats?.hotLeads || 0} hot.`,
        variant: "success",
      });
    } catch (e) {
      setSyncError(e.message);
      push({ title: "Sync failed", desc: e.message, variant: "error" });
    } finally {
      setSyncing(false);
    }
  }, [id, push, buildPatch]);

  // Daily-sync health is read from the SAVED campaign, not the edit buffer:
  // the scheduler only ever sees what has been written to Firestore, so an
  // unsaved toggle must not claim the daily run is on.
  const savedSync = dailySyncState(savedCampaign, {
    credits: Number(userProfile?.credits ?? 0),
    autoSyncEnabled: userProfile?.autoSyncEnabled,
  });
  const autoSyncUnsaved =
    !!savedCampaign &&
    !!campaign &&
    !!savedCampaign.autoSyncDaily !== !!campaign.autoSyncDaily;

  // The sources this campaign searches. Campaigns created before source
  // selection was stored fall back to every source, matching what the back
  // end searches for them.
  const selectedPlatforms = campaignPlatforms(campaign);
  const platformLabel = campaignPlatformsLabel(campaign);

  // Sync cost scales with the campaign's lookback window: 15 credits for the
  // 3-hour minimum, 100 credits at the 90-day maximum, linearly interpolated,
  // plus a surcharge for each selected source we pay a provider for (Reddit
  // today). Keep this in sync with collavueSyncCost in functions/index.js.
  const usesReddit = selectedPlatforms.includes("reddit");
  const redditSurcharge = usesReddit
    ? collavueRedditSurcharge(campaign?.dateRangeDays)
    : 0;
  const syncCost = collavueSyncCost(campaign?.dateRangeDays, selectedPlatforms);
  const baseSyncCost = syncCost - redditSurcharge;

  // Toggling a source keeps at least one selected — a campaign with no source
  // has nothing to sync.
  const togglePlatform = (key) => {
    const next = selectedPlatforms.includes(key)
      ? selectedPlatforms.filter((p) => p !== key)
      : [...selectedPlatforms, key];
    if (next.length === 0) return;
    touchedRef.current.add("platforms");
    setCampaign((c) => ({
      ...c,
      platforms: LEAD_PLATFORMS.map((p) => p.v).filter((p) => next.includes(p)),
    }));
  };

  const isDirty =
    !!savedCampaign &&
    !!campaign &&
    [...touchedRef.current].some(
      (k) => JSON.stringify(campaign[k]) !== JSON.stringify(savedCampaign[k])
    );

  useEffect(() => {
    if (!changesMadeToastShown.current && isDirty) {
      push({
        title: "Changes made.",
        desc: "Please scroll to top to save changes.",
        variant: "info",
      });
      changesMadeToastShown.current = true;
    }
  }, [isDirty])

  useEffect(() => {
    if (loading || !campaign) return;
    if (autoSyncFiredRef.current) return;
    if (params.get("autoSync") !== "1") return;
    autoSyncFiredRef.current = true;
    const next = new URLSearchParams(params);
    next.delete("autoSync");
    setParams(next, { replace: true });
    // Auto-trigger the manual sync confirmation modal now that platforms are
    setConfirmSyncOpen(true);
  }, [loading, campaign, params, setParams]);


  if (loading)
    return (
      <div className="p-10 text-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin inline" />
      </div>
    );
  if (!campaign)
    return (
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <PageHeader title="Campaign not found" />
        <Link to="/campaigns" className="text-indigo-600">
          ← Back to campaigns
        </Link>
      </div>
    );

  const onSave = async () => {
    setSaving(true);
    try {
      await updateCampaign(id, buildPatch());
      touchedRef.current = new Set();
      changesMadeToastShown.current = false;
      push({ title: "Saved", variant: "success" });
      return true;
    } catch (e) {
      push({ title: "Save failed", desc: e.message, variant: "error" });
      return false;
    } finally {
      setSaving(false);
    }
  };



  const onRunSyncClick = () => {
    if (isDirty) {
      setUnsavedSyncOpen(true);
    } else {
      setConfirmSyncOpen(true);
    }
  };

  const onSaveThenSync = async () => {
    const ok = await onSave();
    if (!ok) return;
    setUnsavedSyncOpen(false);
    setConfirmSyncOpen(true);
  };

  const onSyncWithoutSaving = () => {
    setUnsavedSyncOpen(false);
    setConfirmSyncOpen(true);
  };

  // The sync reads the campaign's sources from Firestore, so a source picked
  // in the confirmation dialog has to be persisted before the sync is
  // dispatched — otherwise the run would quietly use the previous selection.
  // Only the source list is written here: the user may have already chosen to
  // sync without saving their other edits, and that choice stands.
  const onConfirmSync = async () => {
    const picked = campaignPlatforms(campaign);
    const saved = campaignPlatforms(savedCampaign);
    if (picked.join(",") !== saved.join(",")) {
      setSaving(true);
      try {
        await updateCampaign(id, { platforms: picked });
        touchedRef.current.delete("platforms");
      } catch (e) {
        push({ title: "Couldn't save sources", desc: e.message, variant: "error" });
        setSaving(false);
        return;
      }
      setSaving(false);
    }
    setConfirmSyncOpen(false);
    onSync();
  };

  const onDelete = async () => {
    setDeleting(true);
    try {
      let deletedLeads = 0;
      if (deleteLeadsToo) {
        deletedLeads = await deleteLeadsByCampaign(id);
      }
      await deleteCampaign(id);
      push({
        title: "Campaign deleted",
        desc:
          deleteLeadsToo && deletedLeads > 0
            ? `${deletedLeads} lead${deletedLeads === 1 ? "" : "s"} also removed.`
            : undefined,
      });
      nav("/campaigns");
    } catch (e) {
      push({ title: "Delete failed", desc: e.message, variant: "error" });
      setDeleting(false);
      setAskDelete(false);
    }
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setAskDelete(false);
    setDeleteLeadsToo(false);
  };

  const closeOverlay = () => {
    const hadResult = !!syncResult;
    setSyncOpen(false);
    setSyncResult(null);
    setSyncError(null);
    // After a successful sync, drop the user into the leads inbox filtered
    // by this campaign so they immediately see the freshly-synced leads
    // without re-applying the filter.
    if (hadResult) {
      try {
        window.localStorage.setItem(FILTER_STORAGE_KEY, id);
      } catch { }
      nav(`/leads?campaignId=${id}`);
    }
  };

  const setField = (k, v) => {
    touchedRef.current.add(k);
    setCampaign((c) => ({ ...c, [k]: v }));
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 bg-[#fafafa] min-h-screen font-sans text-[#0B1038]">
      <SyncAnimationOverlay
        open={syncOpen}
        campaign={campaign}
        result={syncResult}
        error={syncError}
        onClose={closeOverlay}
      />
      <ConfirmDialog
        open={askDelete}
        title="Delete this campaign?"
        body={
          <div className="space-y-3">
            <p>
              The campaign will be permanently removed.
              {leads.length === 0
                ? " It hasn't generated any leads."
                : " By default the leads it generated stay in your inbox."}
            </p>
            {leads.length > 0 && (
              <label className="flex items-start gap-2.5 cursor-pointer select-none rounded-lg border border-gray-200 bg-gray-50 p-3">
                <input
                  type="checkbox"
                  checked={deleteLeadsToo}
                  onChange={(e) => setDeleteLeadsToo(e.target.checked)}
                  disabled={deleting}
                  className="mt-0.5 w-4 h-4 accent-rose-600"
                />
                <span className="text-sm text-gray-700">
                  Also delete the {leads.length} lead
                  {leads.length === 1 ? "" : "s"} generated by this campaign.{" "}
                  <span className="text-gray-500">This can't be undone.</span>
                </span>
              </label>
            )}
          </div>
        }
        confirmLabel={deleteLeadsToo ? "Delete campaign & leads" : "Delete campaign"}
        variant="danger"
        loading={deleting}
        onConfirm={onDelete}
        onCancel={closeDeleteDialog}
      />
      <Link
        to="/campaigns"
        className="text-sm text-gray-600 hover:text-[#0B1038] inline-flex items-center gap-1 mb-3"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>

      <div className="bg-white border border-gray-200 rounded-md p-5 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="font-display text-3xl text-[#0B1038] tracking-tight truncate">
              {campaign.name}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
              {campaign.companyName && (
                <span className="inline-flex items-center gap-1">
                  <Building2 className="w-3.5 h-3.5" />
                  {campaign.companyName}
                </span>
              )}
              {campaign.industry && (
                <span className="inline-flex items-center gap-1">
                  <Globe className="w-3.5 h-3.5" />
                  {campaign.industry}
                </span>
              )}
              {campaign.dateRangeDays && (
                <span className="inline-flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  Last {campaign.dateRangeDays}d
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/leads?campaignId=${id}`}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold hover:bg-slate-50"
            >
              <Inbox className="w-4 h-4" /> Leads
              <span className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-slate-100 text-xs">
                {leads.length}
              </span>
            </Link>
            {isDirty && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                Unsaved changes
              </span>
            )}
            <button
              onClick={onSave}
              disabled={saving || !isDirty}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-semibold disabled:opacity-60 ${isDirty
                ? "border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100"
                : "border-slate-300 hover:bg-slate-50"
                }`}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save
            </button>
            <button
              onClick={onRunSyncClick}
              disabled={syncing || saving}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-sm bg-[#0B1038] text-white text-sm font-semibold hover:bg-[#151c54] disabled:opacity-60 shadow-sm"
            >
              {syncing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Run sync
              <span className="text-xs font-medium opacity-80">({syncCost} cr)</span>
            </button>
            <button
              onClick={() => setAskDelete(true)}
              title="Delete campaign"
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      {unsavedSyncOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-slate-900">
                  Unsaved campaign changes
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  You've edited this campaign but haven't saved yet. If you sync now,
                  your unsaved changes won't be used by this sync.
                </p>
              </div>
            </div>
            <div className="mt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                className="px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50"
                onClick={() => setUnsavedSyncOpen(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50"
                onClick={onSyncWithoutSaving}
                disabled={saving}
              >
                Continue without saving
              </button>
              <button
                className="inline-flex items-center justify-center gap-2 px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-60"
                onClick={onSaveThenSync}
                disabled={saving}
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Save then sync
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmSyncOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-5">
            <h3 className="text-lg font-semibold text-slate-900">Run sync now?</h3>
            <p className="text-sm text-slate-600 mt-2">
              This sync will search {platformLabel} for posts that match your
              keywords inside your configured date range.
            </p>
            {/* Sources are editable right here so the user can decide where to
                look without leaving the confirmation. The selection is saved
                to the campaign before the sync runs — the back end reads it
                from Firestore, not from this request. */}
            <div className="mt-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                Search these sources
              </div>
              <div className="flex flex-wrap gap-2">
                {LEAD_PLATFORMS.map((pf) => {
                  const on = selectedPlatforms.includes(pf.v);
                  return (
                    <button
                      key={pf.v}
                      type="button"
                      onClick={() => togglePlatform(pf.v)}
                      aria-pressed={on}
                      className={`px-3 py-1.5 rounded-lg border text-sm transition ${on
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "border-slate-300 hover:bg-slate-50"
                        }`}
                    >
                      {pf.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mt-3 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div>{platformLabel} search: {baseSyncCost} credits</div>
              {usesReddit && (
                <div className="mt-1">
                  Reddit API surcharge: {redditSurcharge} credits
                  <span className="block text-xs text-slate-500">
                    Reddit is searched through a paid API billed per request.
                  </span>
                </div>
              )}
              <div className="font-semibold mt-1">Total: {syncCost} credits</div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                className="px-3 py-2 text-sm border rounded-lg"
                disabled={saving}
                onClick={() => setConfirmSyncOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg disabled:opacity-60"
                disabled={saving}
                onClick={onConfirmSync}
              >
                {saving ? "Saving…" : "Sync now"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <Card icon={Building2} title="Business profile">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field
                label="Company name"
                value={campaign.companyName}
                onChange={(v) => setField("companyName", v)}
              />
              <Field
                label="Industry"
                value={campaign.industry}
                onChange={(v) => setField("industry", v)}
              />
              <Field
                label="Offer"
                value={campaign.offer}
                onChange={(v) => setField("offer", v)}
              />
              <Field
                label="Target customer"
                value={campaign.targetCustomer}
                onChange={(v) => setField("targetCustomer", v)}
              />
            </div>
            <Field
              label="Short description"
              value={campaign.shortDescription}
              onChange={(v) => setField("shortDescription", v)}
              textarea
            />
          </Card>

          <Card icon={Target} title="Discovery strategy">
            <p className="text-xs text-slate-500 -mt-1 mb-3">
              These keyword sets power lead discovery and scoring. Tune them
              to improve match quality.
            </p>
            <ChipField
              label="Primary keywords"
              value={campaign.primaryKeywords || []}
              onChange={(v) => setField("primaryKeywords", v)}
              accent="indigo"
            />
            <ChipField
              label="Buying intent phrases"
              value={campaign.buyingIntentPhrases || []}
              onChange={(v) => setField("buyingIntentPhrases", v)}
              accent="emerald"
            />
            <ChipField
              label="Problem keywords"
              value={campaign.problemKeywords || []}
              onChange={(v) => setField("problemKeywords", v)}
              accent="amber"
            />
            <ChipField
              label="Competitor keywords"
              value={campaign.competitorKeywords || []}
              onChange={(v) => setField("competitorKeywords", v)}
              accent="violet"
            />
            <ChipField
              label="Negative keywords"
              value={campaign.negativeKeywords || []}
              onChange={(v) => setField("negativeKeywords", v)}
              accent="rose"
            />
          </Card>


          <Card icon={Sparkles} title="Lead rules & response">
            <Field
              label="Explicit lead description"
              value={campaign.explicitLeadDescription}
              onChange={(v) => setField("explicitLeadDescription", v)}
              textarea
            />
            <Field
              label="What we offer"
              value={campaign.whatWeOffer}
              onChange={(v) => setField("whatWeOffer", v)}
              textarea
            />
            <ChipField
              label="Response guidelines"
              value={campaign.responseGuidelines || []}
              onChange={(v) => setField("responseGuidelines", v)}
              accent="emerald"
            />
            <ChipField
              label="Disallowed reply behavior"
              value={campaign.disallowedResponseBehavior || []}
              onChange={(v) => setField("disallowedResponseBehavior", v)}
              accent="rose"
            />
          </Card>
        </div>

        <div className="space-y-5">
          <Card icon={Settings2} title="Sync settings">
            <div className="space-y-5">
              <div>
                <Label>Campaign goal</Label>
                <p className="text-xs text-slate-500 mb-2">
                  What you want out of your leads — steers what the AI
                  searches for and how it scores each post on future syncs.
                </p>
                <div className="flex flex-wrap gap-2">
                  {CAMPAIGN_GOALS.map((g) => (
                    <button
                      key={g.v}
                      onClick={() => setField("campaignGoal", g.v)}
                      title={g.desc}
                      className={`px-3 py-1.5 rounded-lg border text-sm transition ${(campaign.campaignGoal || "sales") === g.v
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "border-slate-300 hover:bg-slate-50"
                        }`}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  {
                    (
                      CAMPAIGN_GOALS.find(
                        (g) => g.v === (campaign.campaignGoal || "sales")
                      ) || CAMPAIGN_GOALS[0]
                    ).desc
                  }
                </p>
              </div>

              <div>
                <Label>Search platforms</Label>
                <p className="text-xs text-slate-500 mb-2">
                  Where each sync looks for leads. Every source you pick is
                  searched in the same run, for the same credit cost, and each
                  is capped separately so one source never crowds out another.
                </p>
                <div className="flex flex-wrap gap-2">
                  {LEAD_PLATFORMS.map((pf) => {
                    const on = selectedPlatforms.includes(pf.v);
                    return (
                      <button
                        key={pf.v}
                        type="button"
                        onClick={() => togglePlatform(pf.v)}
                        aria-pressed={on}
                        className={`px-3 py-1.5 rounded-lg border text-sm transition ${on
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "border-slate-300 hover:bg-slate-50"
                          }`}
                      >
                        {pf.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Keep at least one selected.
                </p>
              </div>

              <div>
                <Label>Date range</Label>
                <p className="text-xs text-slate-500 mb-2">
                  How far back each sync looks for posts.
                </p>
                <div className="flex flex-wrap gap-2">
                  {[0.125, 1, 7, 30, 90].map((d) => (
                    <button
                      key={d}
                      onClick={() => setField("dateRangeDays", d)}
                      className={`px-3 py-1.5 rounded-lg border text-sm transition ${Number(campaign.dateRangeDays) === d
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "border-slate-300 hover:bg-slate-50"
                        }`}
                    >
                      {d < 1 ? "3h" : `${d}d`}
                    </button>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <input
                    type="number"
                    min={0.125}
                    max={90}
                    step={0.125}
                    value={campaign.dateRangeDays || ""}
                    onChange={(e) => {
                      const n = Math.max(
                        0.125,
                        Math.min(90, Number(e.target.value) || 0.125)
                      );
                      setField("dateRangeDays", n);
                    }}
                    className="w-20 px-2 py-1.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                  />
                  <span className="text-xs text-slate-500">custom days</span>
                </div>
                <p className="text-xs text-slate-500 mt-2">Min 3 hours, max 90 days.</p>
              </div>

              <div>
                <Label>Daily auto-sync</Label>
                <p className="text-xs text-slate-500 mb-2">
                  When on, Collavue searches for new leads once a day and
                  emails you a digest — new leads, hot-lead count, and the
                  results of your saved lead-assistant commands. The same{" "}
                  {syncCost} credits are charged per run, and every email has
                  a one-click unsubscribe.
                </p>
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!campaign.autoSyncDaily}
                    onChange={(e) => setField("autoSyncDaily", e.target.checked)}
                    className="w-4 h-4 accent-indigo-600"
                  />
                  <span className="text-sm text-slate-700">
                    {campaign.autoSyncDaily
                      ? "Daily auto-sync is on"
                      : "Daily auto-sync is off"}
                  </span>
                </label>
                <div
                  className={`mt-2 flex items-start gap-2 text-xs leading-relaxed ${
                    savedSync.tone === "on"
                      ? "text-emerald-700"
                      : savedSync.tone === "warn"
                        ? "text-amber-700"
                        : "text-slate-500"
                  }`}
                >
                  <span
                    className={`mt-1 w-2 h-2 shrink-0 rounded-full ${
                      savedSync.tone === "on"
                        ? "bg-emerald-500"
                        : savedSync.tone === "warn"
                          ? "bg-amber-500"
                          : "bg-slate-300"
                    }`}
                  />
                  <span>
                    <strong className="font-semibold">{savedSync.label}.</strong>{" "}
                    {savedSync.detail}
                  </span>
                </div>
                {autoSyncUnsaved && (
                  <p className="mt-1 text-xs text-amber-700">
                    Not saved yet — save this campaign to apply the change to
                    the next daily run.
                  </p>
                )}
              </div>


              <div className="rounded-xl bg-indigo-50/60 border border-indigo-100 p-3 text-xs text-indigo-900">
                Each sync costs <strong>{syncCost} credits</strong>.
              </div>

              {leads.length > 0 && leads.filter((l) => Number(l.intentScore || 0) < 10).length / leads.length >= 0.7 && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                  Leads are consistently low-intent. Add more specific details to this campaign — especially <strong>Short description</strong>, offer, target customer, and competitor keywords — then run sync again.
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5">
      <h2 className="font-semibold flex items-center gap-2 mb-4 text-slate-900">
        {Icon && <Icon className="w-4 h-4 text-indigo-600" />}
        {title}
      </h2>
      {children}
    </div>
  );
}

function Label({ children }) {
  return (
    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">
      {children}
    </label>
  );
}

function Field({ label, value, onChange, textarea }) {
  return (
    <div className="mt-3 first:mt-0">
      <Label>{label}</Label>
      {textarea ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
        />
      ) : (
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
        />
      )}
    </div>
  );
}

const CHIP_ACCENTS = {
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-100",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
  amber: "bg-amber-50 text-amber-700 border-amber-100",
  violet: "bg-violet-50 text-violet-700 border-violet-100",
  rose: "bg-rose-50 text-rose-700 border-rose-100",
  sky: "bg-sky-50 text-sky-700 border-sky-100",
};

function ChipField({ label, value, onChange, accent = "indigo", placeholder }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim();
    if (!v) return;
    onChange([...(value || []), v]);
    setInput("");
  };
  const cls = CHIP_ACCENTS[accent] || CHIP_ACCENTS.indigo;
  return (
    <div className="mt-4 first:mt-0">
      <Label>{label}</Label>
      {(value || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {value.map((c, i) => (
            <span
              key={`${c}-${i}`}
              className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full border ${cls}`}
            >
              {c}
              <button
                type="button"
                onClick={() => onChange(value.filter((_, j) => j !== i))}
                className="hover:opacity-70"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder || "Add and press Enter"}
          className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
        />
        <button
          type="button"
          onClick={add}
          className="px-3 py-2 rounded-lg border border-slate-300 text-sm hover:bg-slate-50 inline-flex items-center gap-1"
        >
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
    </div>
  );
}
